import type { FileEdit } from '../plan/edits';
import { withItemsAdded } from '../membership/membership';
import type { Item } from '../membership/membership';
import { newUlid } from '../entity/catalog';
import { newNookFile, newNookPath } from './workspace';
import type { Workspace } from './workspace';

/** How long a scratch canvas stays the current one after it last changed:
 *  a week, the same weekday a week on still counting. */
const CURRENT_FOR_DAYS = 7;

/** Days since the epoch, on the local calendar. */
function localDay(ms: number): number {
  const d = new Date(ms);
  return Math.floor((ms - d.getTimezoneOffset() * 60_000) / 86_400_000);
}

const byUpdated = (a: Workspace, b: Workspace) =>
  (b.updatedMs ?? 0) - (a.updatedMs ?? 0) || a.path.localeCompare(b.path);

/** The scratch canvases, most recently changed first: the live ones, and the
 *  ones put away. One held inside another nook is that nook's, in neither. */
export function scratchCanvases(workspaces: readonly Workspace[]): { live: Workspace[]; away: Workspace[] } {
  const canvases = workspaces.filter((w) => w.scratch && !w.inside);
  return {
    live: canvases.filter((w) => !w.archived).sort(byUpdated),
    away: canvases.filter((w) => w.archived).sort(byUpdated),
  };
}

/**
 * The scratch canvas a send lands in: the live one changed most recently, if
 * that was within [CURRENT_FOR_DAYS] days of [nowMs]. Opening one changes
 * nothing, so only a change keeps it current. Null when none is: the next
 * send starts a new one.
 */
export function currentScratch(workspaces: readonly Workspace[], nowMs: number = Date.now()): Workspace | null {
  const latest = scratchCanvases(workspaces).live[0];
  if (!latest || latest.updatedMs === null) return null;
  return localDay(nowMs) - localDay(latest.updatedMs) <= CURRENT_FOR_DAYS ? latest : null;
}

/** A scratch canvas's first file: a nook marked so. No title, since it has no name. */
export function newScratchFile(id: string, at: Date): string {
  return newNookFile(id, at).replace('kind: workspace\n', 'kind: workspace\nscratch: true\n');
}

/** A new scratch canvas already holding [items], in one file. */
export function newScratchHolding(
  items: readonly Item[],
  taken: (path: string) => boolean,
  now: Date = new Date(),
): FileEdit {
  const path = newNookPath(now, taken);
  const raw = newScratchFile(newUlid(now.getTime()), now);
  return { path, text: withItemsAdded({ kind: 'nook', path, raw }, items) };
}
